import React, { useEffect, useMemo, useRef, useState } from "react";
import useApi from "../../CustomHooks/useApi";
import INDIAN_BANKS from "./indianBanks";
import {
  Landmark, Users, UserX, Pencil, Ban, CheckCircle2,
  Trash2, Settings, CreditCard, Banknote, Globe, Plus,
  ChevronDown, ChevronUp, ChevronRight, QrCode, Scale
} from "lucide-react";

const inp = "w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0A2A4F] focus:bg-white transition";

const SECTION_STYLE = {
  CDM:    { bg: "bg-blue-600",   light: "bg-blue-50",   text: "text-blue-700",   border: "border-blue-200",   badge: "bg-blue-100 text-blue-700",   Icon: CreditCard, label: "Cash Deposit Machine" },
  CASH:   { bg: "bg-amber-500",  light: "bg-amber-50",  text: "text-amber-700",  border: "border-amber-200",  badge: "bg-amber-100 text-amber-700",  Icon: Banknote,   label: "Cash Deposit"         },
  ONLINE: { bg: "bg-violet-600", light: "bg-violet-50", text: "text-violet-700", border: "border-violet-200", badge: "bg-violet-100 text-violet-700", Icon: Globe,      label: "Online Transfer"      },
  QR:     { bg: "bg-emerald-600",light: "bg-emerald-50",text: "text-emerald-700",border: "border-emerald-200",badge: "bg-emerald-100 text-emerald-700",Icon: QrCode,    label: "QR Payment"           },
};

const SECTIONS = ["CDM", "CASH", "ONLINE", "QR"];

// Self-contained slider — uncontrolled number input so cursor moves freely
const WeightSlider = React.memo(({ initialValue, onChange }) => {
  const [val, setVal] = useState(Math.min(100, Math.max(0, Number(initialValue) || 0)));
  const inputRef      = useRef(null);

  const commit = (raw) => {
    const n = Math.min(100, Math.max(0, parseInt(raw, 10) || 0));
    setVal(n);
    if (inputRef.current) inputRef.current.value = String(n);
    onChange(n);
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <input
          type="range" min="0" max="100" step="1"
          value={val}
          onChange={(e) => {
            const n = Number(e.target.value);
            setVal(n);
            if (inputRef.current) inputRef.current.value = String(n);
            onChange(n);
          }}
          className="flex-1 cursor-pointer h-2 rounded-full appearance-none outline-none"
          style={{
            background: `linear-gradient(to right, #6366f1 ${val}%, #e0e7ff ${val}%)`
          }}
        />
        {/* Uncontrolled — React never touches value after mount, so cursor is free */}
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          maxLength={3}
          defaultValue={String(val)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { commit(inputRef.current.value); e.preventDefault(); }
            if (!/[0-9]/.test(e.key) && !["Backspace","Delete","ArrowLeft","ArrowRight","Home","End","Tab"].includes(e.key)) {
              e.preventDefault();
            }
          }}
          onBlur={() => commit(inputRef.current.value)}
          className="w-14 text-center bg-white border border-indigo-200 rounded-lg px-1 py-1 text-xs font-black text-indigo-700 outline-none focus:ring-1 focus:ring-indigo-300"
        />
        <span className="text-[10px] font-bold text-indigo-600">%</span>
      </div>
    </>
  );
});

// ── SectionConfigForm — module-level with LOCAL state so typing never touches parent ──
const SectionConfigForm = ({
  s, secKey, cardBorder, cardLight, cardText, cardBg, isActive,
  initialData, configuringKey, savingSection2,
  onSave, onCancel,
}) => {
  const [feeType,    setFeeType]    = useState(initialData.feeType    || "FLAT");
  const [feeValue,   setFeeValue]   = useState(String(initialData.feeValue   ?? 0));
  const [tpFeeType,  setTpFeeType]  = useState(initialData.tpFeeType  || "FLAT");
  const [tpFeeValue, setTpFeeValue] = useState(String(initialData.tpFeeValue ?? 0));
  const [weight,     setWeight]     = useState(initialData.routingWeight ?? 100);

  return (
    <div className={`rounded-xl border ${cardBorder} p-3 space-y-2 ${cardLight}`}>
      <p className={`text-[10px] font-black uppercase tracking-widest ${cardText}`}>Fee — {s}</p>
      <select value={feeType} onChange={(e) => setFeeType(e.target.value)}
        className="w-full text-xs rounded-lg border border-gray-200 px-2 py-1.5 outline-none bg-white">
        <option value="FLAT">Flat (₹)</option>
        <option value="PERCENTAGE">Percentage (%)</option>
      </select>
      <input type="number" min="0" step="0.01" placeholder="Fee value"
        value={feeValue} onChange={(e) => setFeeValue(e.target.value)}
        className="w-full text-xs rounded-lg border border-gray-200 px-2 py-1.5 outline-none bg-white" />
      <div className="border-t border-white/60 pt-2">
        <p className="text-[10px] font-black uppercase tracking-widest text-orange-600 mb-1.5">TP Fee — {s}</p>
        <select value={tpFeeType} onChange={(e) => setTpFeeType(e.target.value)}
          className="w-full text-xs rounded-lg border border-gray-200 px-2 py-1.5 outline-none bg-white mb-1.5">
          <option value="FLAT">Flat (₹)</option>
          <option value="PERCENTAGE">Percentage (%)</option>
        </select>
        <input type="number" min="0" step="0.01" placeholder="TP fee value"
          value={tpFeeValue} onChange={(e) => setTpFeeValue(e.target.value)}
          className="w-full text-xs rounded-lg border border-gray-200 px-2 py-1.5 outline-none bg-white" />
      </div>
      <div className="border-t border-white/60 pt-2">
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[10px] font-black uppercase tracking-widest text-indigo-600 flex items-center gap-1">
            <Scale size={11} /> Routing Weight
          </p>
          <span className="text-[10px] text-gray-400">0–100</span>
        </div>
        <WeightSlider key={configuringKey} initialValue={weight} onChange={setWeight} />
      </div>
      <div className="flex gap-1.5 pt-1">
        <button
          onClick={() => onSave({ feeType, feeValue: parseFloat(feeValue) || 0, tpFeeType, tpFeeValue: parseFloat(tpFeeValue) || 0, routingWeight: weight })}
          disabled={savingSection2 === secKey}
          className={`flex-1 py-1.5 rounded-lg text-white text-[10px] font-bold ${isActive ? cardBg : "bg-gray-500"} hover:opacity-90 transition`}>
          {savingSection2 === secKey ? "Saving…" : "Save"}
        </button>
        <button onClick={onCancel}
          className="flex-1 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-500 text-[10px] font-bold hover:bg-gray-50 transition">
          Cancel
        </button>
      </div>
    </div>
  );
};

// Responsive column-count hook
const useColCount = (breakpoints) => {
  const [cols, setCols] = useState(1);
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      let c = breakpoints[0].cols;
      for (const bp of breakpoints) { if (w >= bp.minWidth) c = bp.cols; }
      setCols(c);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  return cols;
};

const chunkArray = (arr, size) => {
  const rows = [];
  for (let i = 0; i < arr.length; i += size) rows.push(arr.slice(i, i + size));
  return rows;
};

const Bankdetails = () => {
  const [bankGroups,   setBankGroups]   = useState([]);   // List<MainBankResponse>
  const [loading,      setLoading]      = useState(false);
  const [creating,     setCreating]     = useState(false);
  const [togglingId,   setTogglingId]   = useState(null);
  const [deletingId,   setDeletingId]   = useState(null);
  const [showAddForm,  setShowAddForm]  = useState(false);
  const [openBank,     setOpenBank]     = useState(null); // mainBank string or null
  const [openNick,     setOpenNick]     = useState(null); // nick id or null
  const [configNick,   setConfigNick]   = useState(null); // nick being configured
  const [sectionDraft, setSectionDraft] = useState({});   // { CDM: { isActive, feeType, feeValue } }
  const [savingSection,setSavingSection]= useState(null); // section name being saved
  const [confirmPopup,   setConfirmPopup]   = useState({ open: false, action: "", title: "", message: "", onConfirm: null });
  const [pendingSection, setPendingSection] = useState(null); // { nickId, section } — avoids stale closure in delete
  const [formError,    setFormError]    = useState("");
  const [form,         setForm]         = useState({ mainBank: "", nickName: "", accountNumber: "", ifscCode: "", sections: ["CDM","CASH","ONLINE","QR"] });
  const [isCustomBank, setIsCustomBank] = useState(false);
  const [bankSearch,   setBankSearch]   = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef                     = useRef(null);

  // quick-add form per bank group
  const [quickAdd,      setQuickAdd]      = useState(null);
  const [quickFormError,setQuickFormError]= useState("");
  const [quickForm,     setQuickForm]     = useState({ nickName: "", accountNumber: "", ifscCode: "", sections: ["CDM","CASH","ONLINE","QR"] });
  const [quickCreating, setQuickCreating] = useState(false);

  // ── Lifted NickRow state (keyed by nickId or "nickId_SECTION") ────────────
  const [editingNickId,    setEditingNickId]    = useState(null);
  const [savingDetails,    setSavingDetails]    = useState(false);
  // Uncontrolled refs for account/IFSC inputs — avoids re-render on every keystroke
  const nickNameInputRef   = useRef(null);
  const accountInputRef    = useRef(null);
  const ifscInputRef       = useRef(null);
  // Uncontrolled refs for fee value inputs — same reason (NickRow remounts on every parent state change)
  const feeValueInputRef   = useRef(null);
  const tpFeeValueInputRef = useRef(null);
  const [configuringKey,   setConfiguringKey]   = useState(null);           // "nickId_SECTION"
  const [sectionFormData,  setSectionFormData]  = useState({ feeType: "FLAT", feeValue: 0, tpFeeType: "FLAT", tpFeeValue: 0 });
  const [weightDraft,      setWeightDraft]      = useState(100);
  const [togglingSection,  setTogglingSection]  = useState(null);           // "nickId_SECTION"
  const [savingSection2,   setSavingSection2]   = useState(null);           // "nickId_SECTION"
  const [deletingSection,  setDeletingSection]  = useState(null);           // "nickId_SECTION"
  const [addingSection,    setAddingSection]    = useState(null);           // "nickId_SECTION"


  // Responsive column counts for grid-row expansion
  const bankCols = useColCount([{ minWidth: 0, cols: 1 }, { minWidth: 640, cols: 2 }, { minWidth: 1280, cols: 3 }]);
  const nickCols = useColCount([{ minWidth: 0, cols: 1 }, { minWidth: 640, cols: 2 }, { minWidth: 1024, cols: 3 }, { minWidth: 1280, cols: 4 }]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Filtered bank list for dropdown
  const filteredBanks = INDIAN_BANKS.filter((b) =>
    b.name.toLowerCase().includes(bankSearch.toLowerCase()) ||
    b.short.toLowerCase().includes(bankSearch.toLowerCase())
  );

  const selectedBank = INDIAN_BANKS.find((b) => b.name.toLowerCase() === form.mainBank?.toLowerCase());

  const { callApi: getAllBanks }        = useApi("/admin/cashin/banks/grouped",            "GET");
  const { callApi: createNick }         = useApi("/admin/cashin/bank/nick",               "POST");
  const { callApi: toggleNick }         = useApi("/admin/cashin/bank/nick/toggle",        "PUT");
  const { callApi: updateSection }      = useApi("/admin/cashin/bank/nick/section",       "PUT");
  const { callApi: deleteNick }         = useApi("/admin/cashin/bank/nick",               "DELETE");
  const { callApi: deleteSectionApi }   = useApi("/admin/cashin/bank/nick/section",       "DELETE");
  const { callApi: addSectionApi }      = useApi("/admin/cashin/bank/nick/section/add",   "POST");
  const { callApi: updateNickDetails }  = useApi("/admin/cashin/bank/nick/details",       "PUT");

  // ── Fetch ─────────────────────────────────────────────────────────────────

  const fetchBanks = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await getAllBanks();
      if (Array.isArray(res)) {
        const sorted = [...res].sort((a, b) =>
          (a.mainBank || "").localeCompare(b.mainBank || "")
        );
        setBankGroups(sorted);
      } else {
        setBankGroups([]);
      }
    } catch (e) { console.error(e); }
    if (!silent) setLoading(false);
  };

  useEffect(() => { fetchBanks(); }, []);

  // ── Sort & Filter ─────────────────────────────────────────────────────────

  const [sortAZ,       setSortAZ]       = useState(true);   // true = A→Z, false = Z→A
  const [filterStatus, setFilterStatus] = useState("all");  // "all" | "active" | "inactive"
  const [letterFilter, setLetterFilter] = useState("");     // "" = all, "A"–"Z" = starts with

  // Letters that have at least one bank matching the current status filter
  const availableLetters = useMemo(() =>
    new Set(
      bankGroups
        .filter((g) => {
          const nicks = g.nicks || [];
          if (filterStatus === "active")   return nicks.some((n) => n.isActive);
          if (filterStatus === "inactive") return nicks.some((n) => !n.isActive);
          return nicks.length > 0;
        })
        .map((g) => (g.mainBank || "")[0]?.toUpperCase())
        .filter(Boolean)
    ),
  [bankGroups, filterStatus]);

  // Auto-clear letter filter if selected letter is no longer available
  useEffect(() => {
    if (letterFilter && !availableLetters.has(letterFilter)) setLetterFilter("");
  }, [availableLetters, letterFilter]);

  const displayedGroups = useMemo(() => {
    let groups = bankGroups
      .map((g) => ({
        ...g,
        nicks: filterStatus === "all"
          ? (g.nicks || [])
          : (g.nicks || []).filter((n) => filterStatus === "active" ? n.isActive : !n.isActive),
      }))
      .filter((g) => g.nicks.length > 0);
    if (letterFilter) {
      groups = groups.filter((g) => (g.mainBank || "")[0]?.toUpperCase() === letterFilter);
    }
    return sortAZ ? groups : [...groups].reverse();
  }, [bankGroups, sortAZ, filterStatus, letterFilter]);

  // ── Stats ─────────────────────────────────────────────────────────────────

  const allNicks   = bankGroups.flatMap((g) => g.nicks || []);
  const totalNicks = allNicks.length;
  const activeNicks= allNicks.filter((n) => n.isActive).length;

  // ── Create nick ───────────────────────────────────────────────────────────

  const handleCreate = async () => {
    const { mainBank } = form;
    if (!mainBank.trim()) { setFormError("Please select a bank."); return; }
    setFormError("");
    setCreating(true);
    let errMsg = null;
    const result = await createNick({ mainBank: mainBank.trim().toUpperCase(), nickName: form.nickName.trim(), accountNumber: "", ifscCode: "", sections: [] })
      .catch((e) => { errMsg = e?.message || "Failed to create bank."; return null; });
    setCreating(false);
    if (errMsg || result === null || result?.message) { setFormError(errMsg || result?.message || "Failed to create bank."); return; }
    setForm({ mainBank: "", nickName: "", accountNumber: "", ifscCode: "", sections: ["CDM","CASH","ONLINE","QR"] });
    setIsCustomBank(false);
    setShowAddForm(false);
    fetchBanks();
  };

  // ── Quick-add nick under a specific bank ─────────────────────────────────

  const handleQuickCreate = async (mainBank) => {
    const { nickName, accountNumber } = quickForm;
    if (!nickName.trim() || !accountNumber.trim()) { setQuickFormError("Please fill all required fields."); return; }
    if (!/^[0-9]+$/.test(accountNumber)) { setQuickFormError("Account number must be numeric."); return; }
    if (quickForm.sections.length === 0) { setQuickFormError("Select at least one section to create."); return; }
    setQuickFormError("");
    setQuickCreating(true);
    let errMsg = null;
    const result = await createNick({ mainBank: mainBank.trim().toUpperCase(), nickName: nickName.trim(), accountNumber: accountNumber.trim(), ifscCode: quickForm.ifscCode.trim(), sections: quickForm.sections })
      .catch((e) => { errMsg = e?.message || "Failed to create sub bank."; return null; });
    setQuickCreating(false);
    if (errMsg || result === null || result?.message) { setQuickFormError(errMsg || result?.message || "Failed to create sub bank."); return; }
    setQuickForm({ nickName: "", accountNumber: "", ifscCode: "", sections: ["CDM","CASH","ONLINE","QR"] });
    setQuickAdd(null);
    setOpenBank(mainBank);
    fetchBanks(true);
  };

  // ── Toggle nick ───────────────────────────────────────────────────────────

  const handleToggle = async (id) => {
    try {
      setTogglingId(id);
      await toggleNick(null, `?id=${id}`);
      fetchBanks(true);
    } catch (e) { console.error(e); }
    finally { setTogglingId(null); }
  };

  // ── Delete nick ───────────────────────────────────────────────────────────

  const handleDelete = async (id) => {
    try {
      setDeletingId(id);
      await deleteNick(null, `?id=${id}`);
      fetchBanks(true);
    } catch (e) { console.error(e); }
    finally { setDeletingId(null); }
  };

  // ── Open configure drawer ─────────────────────────────────────────────────

  const openConfig = (nick) => {
    setConfigNick(nick);
    // Pre-fill draft from existing section data
    const draft = {};
    SECTIONS.forEach((s) => {
      const sec = nick.sections?.[s] || {};
      draft[s] = {
        isActive:   sec.isActive !== false,
        feeType:    sec.feeType  || "FLAT",
        feeValue:   sec.feeValue ?? 0,
        tpFeeType:  sec.tpFeeType  || "FLAT",
        tpFeeValue: sec.tpFeeValue ?? 0,
      };
    });
    setSectionDraft(draft);
  };

  // ── Update section ────────────────────────────────────────────────────────

  const handleSaveSection = async (section) => {
    try {
      setSavingSection(section);
      const d = sectionDraft[section];
      await updateSection({
        id:         configNick.id,
        section,
        isActive:   d.isActive,
        feeType:    d.feeType,
        feeValue:   parseFloat(d.feeValue) || 0,
        tpFeeType:  d.tpFeeType  || "FLAT",
        tpFeeValue: parseFloat(d.tpFeeValue) || 0,
        routingWeight: d.routingWeight ?? 100,
      });
      fetchBanks(true);
    } catch (e) { console.error(e); }
    finally { setSavingSection(null); }
  };

  const updateDraft = (section, key, value) =>
    setSectionDraft((p) => ({ ...p, [section]: { ...p[section], [key]: value } }));

  // ── Nick row — uses PARENT state (no local useState) ─────────────────────

  const NickRow = ({ nick }) => {
    const id              = nick.id;
    const isOpen          = openNick === id;
    const activeSections  = SECTIONS.filter((s) => nick.sections?.[s]?.isActive);
    const bankMeta        = INDIAN_BANKS.find((b) => b.name.toLowerCase() === nick.mainBank?.toLowerCase());
    const isEditingDetails= editingNickId === id;

    // ── handlers ──────────────────────────────────────────────────────────

    const handlePencilClick = () => {
      setEditingNickId(editingNickId === id ? null : id);
    };

    const handleSaveDetails = async () => {
      const nickName      = nickNameInputRef.current?.value?.trim() || "";
      const accountNumber = accountInputRef.current?.value?.trim() || "";
      const ifscCode      = ifscInputRef.current?.value?.trim().toUpperCase() || "";
      if (accountNumber && !/^[0-9]+$/.test(accountNumber)) { alert("Account number must be numeric"); return; }
      try {
        setSavingDetails(true);
        await updateNickDetails({ id, nickName, accountNumber, ifscCode });
        setEditingNickId(null);
        fetchBanks(true);
      } catch (e) { console.error(e); }
      finally { setSavingDetails(false); }
    };

    return (
      <div className={`rounded-2xl border overflow-hidden bg-white ${nick.isActive ? "border-gray-200" : "border-gray-100"}`}>

        {/* Nick card body */}
        <div className="p-4">

          {/* Top: logo + nick name + status badge */}
          <div className="flex items-start gap-3 mb-3">
            {bankMeta ? (
              <div className="w-11 h-11 rounded-xl border border-gray-100 bg-white flex items-center justify-center flex-shrink-0 overflow-hidden p-1 shadow-sm">
                <img src={bankMeta.logo} alt={nick.mainBank} className="w-full h-full object-contain"
                  onError={(e) => { e.target.style.display="none"; e.target.parentNode.innerHTML=`<span class='text-xs font-black text-gray-500'>${nick.nickName?.[0]?.toUpperCase()}</span>`; }} />
              </div>
            ) : (
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center text-sm font-black text-gray-500 flex-shrink-0">
                {nick.nickName?.[0]?.toUpperCase()}
              </div>
            )}

            <div className="flex-1 min-w-0">
              <p className="text-sm font-black text-gray-800 truncate">{nick.nickName}</p>
              <p className="text-[10px] text-gray-400 font-mono mt-0.5 truncate">{nick.accountNumber}</p>
              {nick.ifscCode && (
                <span className="text-[10px] text-gray-500 font-mono bg-gray-100 px-1.5 py-0.5 rounded mt-0.5 inline-block">{nick.ifscCode}</span>
              )}
            </div>

            <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full flex-shrink-0 ${nick.isActive ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-500"}`}>
              {nick.isActive ? "Active" : "Inactive"}
            </span>
          </div>

          {/* Active section badges */}
          <div className="flex flex-wrap gap-1.5 mb-3 min-h-[22px]">
            {activeSections.length > 0
              ? activeSections.map((s) => (
                  <span key={s} className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${SECTION_STYLE[s]?.badge}`}>{s}</span>
                ))
              : <span className="text-[10px] text-gray-400 italic">No active sections</span>
            }
          </div>

          {/* Action buttons row */}
          <div className="flex items-center gap-1.5 mb-3">
            <button onClick={handlePencilClick}
              className={`flex-1 h-8 rounded-xl text-[11px] font-bold transition flex items-center justify-center gap-1 border ${
                isEditingDetails ? "bg-[#0A2A4F] text-white border-[#0A2A4F]" : "bg-white text-gray-500 border-gray-200 hover:border-[#0A2A4F] hover:text-[#0A2A4F]"
              }`}>
              <Pencil size={11} /> Edit
            </button>

            <button
              onClick={() => setConfirmPopup({ open: true, action: nick.isActive ? "deactivate" : "activate", title: nick.isActive ? "Deactivate Nick" : "Activate Nick", message: `Are you sure you want to ${nick.isActive ? "deactivate" : "activate"} "${nick.nickName}"?`, onConfirm: () => handleToggle(id) })}
              disabled={togglingId === id}
              className={`flex-1 h-8 rounded-xl text-[11px] font-bold transition flex items-center justify-center gap-1 border ${
                togglingId === id ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                : nick.isActive ? "bg-white text-red-500 border-red-200 hover:bg-red-50"
                : "bg-white text-emerald-600 border-emerald-200 hover:bg-emerald-50"
              }`}>
              {togglingId === id ? "…" : nick.isActive ? <><Ban size={11} /> Deactivate</> : <><CheckCircle2 size={11} /> Activate</>}
            </button>

            <button
              onClick={() => setConfirmPopup({ open: true, action: "delete", title: "Delete Nick", message: `Delete "${nick.nickName}"? This cannot be undone.`, onConfirm: () => handleDelete(id) })}
              disabled={deletingId === id}
              className="w-8 h-8 rounded-xl transition flex items-center justify-center border bg-white text-gray-400 border-gray-200 hover:bg-red-50 hover:text-red-500 hover:border-red-200 flex-shrink-0">
              {deletingId === id ? <span className="text-[10px]">…</span> : <Trash2 size={13} />}
            </button>
          </div>

          {/* View / Hide Sections button */}
          <button onClick={() => setOpenNick((prev) => (prev === id ? null : id))}
            className={`w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold transition border ${
              isOpen ? "bg-[#0A2A4F] text-white border-[#0A2A4F]" : "bg-gray-50 text-gray-600 border-gray-200 hover:border-[#0A2A4F] hover:text-[#0A2A4F]"
            }`}>
            {isOpen ? <><ChevronUp size={13} /> Hide Sections</> : <><ChevronDown size={13} /> View Sections</>}
          </button>
        </div>

        {/* Edit details panel */}
        {isEditingDetails && (
          <div className="border-t border-[#0A2A4F]/10 px-4 py-4 bg-[#0A2A4F]/[0.03]">

            {/* Header */}
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-8 h-8 rounded-xl bg-[#0A2A4F] flex items-center justify-center flex-shrink-0">
                <Pencil size={13} className="text-white" />
              </div>
              <div>
                <p className="text-xs font-black text-[#0A2A4F] leading-tight">Edit Account</p>
                <p className="text-[10px] text-gray-400 leading-tight">{nick.nickName}</p>
              </div>
            </div>

            {/* Fields */}
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">
                  Nick Name
                </label>
                <input
                  ref={nickNameInputRef}
                  type="text"
                  placeholder="e.g. ICICI-10"
                  defaultValue={nick.nickName || ""}
                  className={inp}
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">
                  Account Number
                </label>
                <input
                  ref={accountInputRef}
                  type="text"
                  placeholder="Numeric only"
                  defaultValue={nick.accountNumber || ""}
                  className={inp}
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">
                  IFSC Code
                </label>
                <input
                  ref={ifscInputRef}
                  type="text"
                  placeholder="e.g. ICIC0001234"
                  defaultValue={nick.ifscCode || ""}
                  className={inp}
                  onInput={(e) => { e.target.value = e.target.value.toUpperCase(); }}
                />
              </div>
            </div>

            {/* Action buttons — same style as card buttons */}
            <div className="flex gap-2">
              <button onClick={handleSaveDetails} disabled={savingDetails}
                className={`flex-1 h-9 rounded-xl text-white text-xs font-bold transition ${savingDetails ? "bg-gray-300 cursor-not-allowed" : "bg-[#0A2A4F] hover:bg-[#0d3562]"}`}>
                {savingDetails ? "Saving…" : "Save Changes"}
              </button>
              <button onClick={() => setEditingNickId(null)}
                className="px-4 h-9 rounded-xl bg-white border border-gray-200 text-gray-600 text-xs font-bold hover:bg-gray-50 transition">
                Cancel
              </button>
            </div>
          </div>
        )}

      </div>
    );
  };

  // ── NickSectionsPanel — renders CDM/CASH/ONLINE/QR cards below the nick grid ──

  const NickSectionsPanel = ({ nick }) => {
    const id          = nick.id;
    const nickInactive= !nick.isActive;

    const handleSectionToggle = async (section, currentIsActive) => {
      const key = `${id}_${section}`;
      try {
        setTogglingSection(key);
        const sec = nick.sections?.[section] || {};
        await updateSection({ id, section, isActive: !currentIsActive, feeType: sec.feeType || "FLAT", feeValue: sec.feeValue ?? 0, routingWeight: sec.routingWeight ?? 100, tpFeeType: sec.tpFeeType || "FLAT", tpFeeValue: sec.tpFeeValue ?? 0 });
        fetchBanks(true);
      } catch (e) { console.error(e); }
      finally { setTogglingSection(null); }
    };

    const openSectionConfigure = (section) => {
      const key = `${id}_${section}`;
      setConfiguringKey(configuringKey === key ? null : key);
    };

    const handleSaveSectionInPanel = async (section, formData) => {
      const key = `${id}_${section}`;
      const w = Math.round(formData.routingWeight);
      if (isNaN(w) || w < 0 || w > 100) { alert("Routing weight must be 0–100"); return; }
      try {
        setSavingSection2(key);
        const sec = nick.sections?.[section] || {};
        await updateSection({ id, section, isActive: sec.isActive !== false, feeType: formData.feeType, feeValue: formData.feeValue, routingWeight: w, tpFeeType: formData.tpFeeType, tpFeeValue: formData.tpFeeValue });
        setConfiguringKey(null);
        fetchBanks(true);
      } catch (e) { console.error(e); }
      finally { setSavingSection2(null); }
    };

    const handleDeleteSection = (section) => {
      setPendingSection({ nickId: id, section });
      setConfirmPopup({ open: true, action: "delete", title: "Delete Section", message: `Remove ${section} section from "${nick.nickName}"?`, onConfirm: null });
    };

    const handleAddSection = async (section) => {
      const key = `${id}_${section}`;
      try {
        setAddingSection(key);
        await addSectionApi(null, `?id=${id}&section=${section}`);
        fetchBanks(true);
      } catch (e) { console.error(e); }
      finally { setAddingSection(null); }
    };

    return (
      <div className="mt-4 pt-4 border-t border-gray-200">
        {/* Panel header */}
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] font-black text-gray-500 uppercase tracking-widest">
            Sections — {nick.nickName}
          </p>
          <button onClick={() => setOpenNick(null)}
            className="text-[10px] font-bold text-gray-400 hover:text-gray-600 px-2 py-1 rounded-lg hover:bg-gray-100 transition">
            ✕ Close
          </button>
        </div>

        {/* Section cards grid — auto-fit so cards fill available width */}
        <div className="grid gap-3 items-start" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
          {SECTIONS.map((s) => {
            const sec        = nick.sections?.[s];
            const exists     = sec !== undefined && sec !== null;
            const st         = SECTION_STYLE[s];
            const isActive   = exists && sec.isActive !== false && !nickInactive;
            const feeDisplay = !exists ? "—" : sec.feeType === "PERCENTAGE" ? `${sec.feeValue ?? 0}%` : `₹${sec.feeValue ?? 0}`;
            const secKey     = `${id}_${s}`;
            const isConfig   = configuringKey === secKey;
            const cardBg     = nickInactive ? "bg-gray-300"    : isActive ? st.bg     : "bg-gray-400";
            const cardBorder = nickInactive ? "border-gray-200": isActive ? st.border : "border-gray-200";
            const cardText   = nickInactive ? "text-gray-400"  : isActive ? st.text   : "text-gray-400";
            const cardLight  = nickInactive ? "bg-gray-100"    : isActive ? st.light  : "bg-gray-100";

            if (!exists) return (
              <div key={s} className={`rounded-2xl border-2 ${st.border} overflow-hidden`}>
                {/* Header — section color but faded */}
                <div className={`${st.light} px-4 py-3 flex items-center justify-between relative overflow-hidden`}>
                  <div className="absolute -right-4 -top-4 w-20 h-20 rounded-full bg-white/40" />
                  <div className="absolute -right-2 -bottom-6 w-16 h-16 rounded-full bg-white/40" />
                  <div className="flex items-center gap-2.5 relative z-10">
                    <div className={`w-8 h-8 rounded-xl bg-white/70 flex items-center justify-center flex-shrink-0`}>
                      <st.Icon size={16} className={st.text} />
                    </div>
                    <div>
                      <p className={`text-sm font-black ${st.text} leading-none`}>{s}</p>
                      <p className={`text-[10px] mt-0.5 ${st.text} opacity-60`}>{st.label}</p>
                    </div>
                  </div>
                  <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full bg-white/70 ${st.text} relative z-10`}>Not Added</span>
                </div>
                {/* Body — same structure, all dashes, flex-1 to fill height */}
                <div className="p-4 space-y-2.5 bg-white">
                  <div className="flex items-center justify-between"><span className="text-[10px] font-bold text-gray-300 uppercase tracking-wide">Account</span><span className="text-xs font-bold text-gray-300">—</span></div>
                  {s !== "QR" && <div className="flex items-center justify-between"><span className="text-[10px] font-bold text-gray-300 uppercase tracking-wide">IFSC</span><span className="text-xs font-bold text-gray-300">—</span></div>}
                  <div className="border-t border-gray-100" />
                  <div className="flex items-center justify-between"><span className="text-[10px] font-bold text-gray-300 uppercase tracking-wide">Fee Type</span><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st.light} ${st.text} opacity-40`}>—</span></div>
                  <div className="flex items-center justify-between"><span className="text-[10px] font-bold text-gray-300 uppercase tracking-wide">Fee Amount</span><span className="text-base font-black text-gray-300">—</span></div>
                  <div className="flex items-center justify-between"><span className="text-[10px] font-bold text-gray-300 uppercase tracking-wide">TP Fee</span><span className="text-xs font-bold text-gray-300">—</span></div>
                  <div className="flex items-center justify-between"><span className="text-[10px] font-bold text-gray-300 uppercase tracking-wide flex items-center gap-1"><Scale size={11} className="text-gray-300" /> Weight</span><span className={`text-xs font-black px-2 py-0.5 rounded-full ${st.light} ${st.text} opacity-40`}>—</span></div>
                  <div className="w-full h-1.5 rounded-full bg-gray-100" />
                  {/* Add button — pinned to bottom */}
                  <div className="pt-1 border-t border-gray-100 mt-1">
                    <button onClick={() => handleAddSection(s)} disabled={addingSection === secKey}
                      className={`w-full py-2 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 ${
                        addingSection === secKey ? "bg-gray-100 text-gray-300 cursor-not-allowed" : `${st.bg} text-white hover:opacity-90`
                      }`}>
                      {addingSection === secKey ? "Adding…" : <><Plus size={13} /> Add {s}</>}
                    </button>
                  </div>
                </div>
              </div>
            );

            return (
              <div key={s} className={`rounded-2xl border-2 overflow-hidden ${cardBorder} ${nickInactive ? "opacity-60" : ""}`}>
                <div className={`${cardBg} px-4 py-3 flex items-center justify-between relative overflow-hidden`}>
                  <div className="absolute -right-4 -top-4 w-20 h-20 rounded-full bg-white/10" />
                  <div className="absolute -right-2 -bottom-6 w-16 h-16 rounded-full bg-white/10" />
                  <div className="flex items-center gap-2.5 relative z-10">
                    <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                      <st.Icon size={16} className="text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-black text-white leading-none">{s}</p>
                      <p className="text-[10px] text-white/70 mt-0.5">{st.label}</p>
                    </div>
                  </div>
                  <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full relative z-10 ${isActive ? "bg-white/25 text-white" : "bg-white/20 text-white/80"}`}>
                    {isActive ? "● Active" : "○ Inactive"}
                  </span>
                </div>

                <div className="p-4 space-y-2.5 bg-white">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Account</span>
                    <span className="text-xs font-bold text-gray-700 font-mono">
                      {s === "QR" ? `•••• •••• ${(nick.accountNumber || "").slice(-4)}` : nick.accountNumber}
                    </span>
                  </div>
                  {s !== "QR" && (
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">IFSC</span>
                      <span className={`text-xs font-bold font-mono ${nick.ifscCode ? "text-gray-700" : "text-gray-300"}`}>{nick.ifscCode || "—"}</span>
                    </div>
                  )}
                  <div className="border-t border-gray-100" />
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Fee Type</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${sec.feeType === "PERCENTAGE" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>{sec.feeType || "FLAT"}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Fee Amount</span>
                    <span className={`text-base font-black ${isActive ? cardText : "text-gray-400"}`}>{feeDisplay}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">TP Fee</span>
                    <span className="text-xs font-bold text-orange-600">
                      {sec.tpFeeType === "PERCENTAGE" ? `${sec.tpFeeValue ?? 0}%` : `₹${sec.tpFeeValue ?? 0}`}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide flex items-center gap-1"><Scale size={11} className="text-indigo-400" /> Weight</span>
                    <span className={`text-xs font-black px-2 py-0.5 rounded-full ${(sec.routingWeight ?? 100) === 0 ? "bg-red-100 text-red-500" : (sec.routingWeight ?? 100) < 50 ? "bg-amber-100 text-amber-700" : "bg-indigo-100 text-indigo-700"}`}>{sec.routingWeight ?? 100}%</span>
                  </div>
                  <div className={`w-full h-1.5 rounded-full ${cardLight}`}>
                    <div className={`h-full rounded-full transition-all duration-500 ${isActive ? cardBg : "bg-gray-300"}`}
                      style={{ width: isActive && !nickInactive ? `${sec.routingWeight ?? 100}%` : "0%" }} />
                  </div>

                  {isConfig && (
                    <SectionConfigForm
                      s={s} secKey={secKey}
                      cardBorder={cardBorder} cardLight={cardLight} cardText={cardText} cardBg={cardBg} isActive={isActive}
                      initialData={{ feeType: sec.feeType || "FLAT", feeValue: sec.feeValue ?? 0, tpFeeType: sec.tpFeeType || "FLAT", tpFeeValue: sec.tpFeeValue ?? 0, routingWeight: sec.routingWeight ?? 100 }}
                      configuringKey={configuringKey} savingSection2={savingSection2}
                      onSave={(formData) => handleSaveSectionInPanel(s, formData)}
                      onCancel={() => setConfiguringKey(null)}
                    />
                  )}

                  <div className="flex gap-1.5 pt-1 border-t border-gray-100 mt-1">
                    <button onClick={() => openSectionConfigure(s)}
                      className={`flex-1 py-2 rounded-xl text-xs font-bold transition border flex items-center justify-center gap-1.5 ${isConfig ? `${cardBg} text-white border-transparent` : `bg-white ${cardText} ${cardBorder} hover:${cardLight}`}`}>
                      <Settings size={13} /> Config
                    </button>
                    <button onClick={() => handleSectionToggle(s, isActive)} disabled={togglingSection === secKey || nickInactive}
                      className={`flex-1 py-2 rounded-xl text-xs font-bold transition border flex items-center justify-center gap-1.5 ${togglingSection === secKey || nickInactive ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed" : isActive ? "bg-white text-red-500 border-red-200 hover:bg-red-50" : `bg-white ${cardText} ${cardBorder} hover:${cardLight}`}`}>
                      {togglingSection === secKey ? <span>…</span> : isActive ? <><Ban size={13} /> Deactivate</> : <><CheckCircle2 size={13} /> Activate</>}
                    </button>
                    <button onClick={() => handleDeleteSection(s)} disabled={deletingSection === secKey}
                      className="px-3 py-2 rounded-xl bg-white text-gray-400 hover:bg-red-50 hover:text-red-500 border border-gray-200 hover:border-red-200 transition flex items-center justify-center">
                      {deletingSection === secKey ? <span className="text-xs">…</span> : <Trash2 size={14} />}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="p-4 sm:p-6 min-h-full bg-[#EEF1F5]">

      {/* Top bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Cash In</p>
          <h1 className="text-2xl font-black text-[#0A2A4F] mt-0.5">Bank Management</h1>
        </div>
        <button onClick={() => { setShowAddForm((p) => !p); setFormError(""); }}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#0A2A4F] text-white rounded-2xl text-sm font-bold hover:bg-[#0d3562] transition shadow-sm">
          {showAddForm ? <><span className="text-lg leading-none">×</span> Close</> : <><Plus size={16} /> Add Main Bank</>}
        </button>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6">
        <div className="relative bg-[#0A2A4F] rounded-3xl p-4 sm:p-5 flex flex-col gap-5 sm:gap-7 min-h-[120px] sm:min-h-[150px] overflow-hidden">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-white/10 flex items-center justify-center flex-shrink-0">
            <Landmark className="text-white w-[18px] h-[18px] sm:w-[22px] sm:h-[22px]" />
          </div>
          <ChevronRight size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40" />
          <div className="min-w-0 mt-auto">
            <p className="text-3xl sm:text-4xl font-black text-white leading-none">{bankGroups.length}</p>
            <p className="text-[11px] sm:text-xs font-bold text-white/55 mt-1.5">Main Banks</p>
          </div>
        </div>
        <div className="relative bg-emerald-500 rounded-3xl p-4 sm:p-5 flex flex-col gap-5 sm:gap-7 min-h-[120px] sm:min-h-[150px] overflow-hidden">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <Users className="text-white w-[18px] h-[18px] sm:w-[22px] sm:h-[22px]" />
          </div>
          <ChevronRight size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50" />
          <div className="min-w-0 mt-auto">
            <p className="text-3xl sm:text-4xl font-black text-white leading-none">{activeNicks}</p>
            <p className="text-[11px] sm:text-xs font-bold text-white/80 mt-1.5">Active Sub Banks</p>
          </div>
        </div>
        <div className="relative bg-red-50 border border-red-100 rounded-3xl p-4 sm:p-5 flex flex-col gap-5 sm:gap-7 min-h-[120px] sm:min-h-[150px] overflow-hidden">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-red-100 flex items-center justify-center flex-shrink-0">
            <UserX className="text-red-500 w-[18px] h-[18px] sm:w-[22px] sm:h-[22px]" />
          </div>
          <ChevronRight size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-300" />
          <div className="min-w-0 mt-auto">
            <p className="text-3xl sm:text-4xl font-black text-red-500 leading-none">{totalNicks - activeNicks}</p>
            <p className="text-[11px] sm:text-xs font-bold text-gray-400 mt-1.5">Inactive Sub Banks</p>
          </div>
        </div>
      </div>

      {/* Add nick form */}
      {showAddForm && (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 mb-6">
          <h2 className="text-sm font-black text-gray-900 mb-4">Add Main Bank</h2>
          <div className="mb-4 max-w-sm">

            {/* ── Bank dropdown ── */}
            <div ref={dropdownRef} className="relative">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">Main Bank *</label>

              {/* Selected / search input */}
              <button type="button" onClick={() => { setShowDropdown((p) => !p); setBankSearch(""); setIsCustomBank(false); }}
                className={`w-full flex items-center gap-2 bg-gray-50 border ${showDropdown ? "border-[#0A2A4F] ring-2 ring-[#0A2A4F] bg-white" : "border-gray-200"} rounded-xl px-3 py-2.5 text-sm text-left transition`}>
                {isCustomBank ? (
                  <span className="flex-1 font-semibold text-gray-800 truncate">{form.mainBank || "Other"}</span>
                ) : selectedBank ? (
                  <>
                    <img src={selectedBank.logo} alt={selectedBank.name}
                      className="w-6 h-6 rounded object-contain flex-shrink-0"
                      onError={(e) => { e.target.style.display = "none"; }} />
                    <span className="flex-1 font-semibold text-gray-800 truncate">{selectedBank.name}</span>
                    <span className="text-[10px] text-gray-400 flex-shrink-0">{selectedBank.category}</span>
                  </>
                ) : (
                  <span className="flex-1 text-gray-400">Select bank…</span>
                )}
                <span className={`text-gray-400 text-xs transition-transform ${showDropdown ? "rotate-180" : ""}`}>▼</span>
              </button>

              {/* Custom bank name input — shown when "Other" is selected */}
              {isCustomBank && (
                <input autoFocus type="text" placeholder="Enter bank name…"
                  value={form.mainBank}
                  onChange={(e) => setForm({ ...form, mainBank: e.target.value })}
                  className="mt-2 w-full bg-white border border-[#0A2A4F] rounded-xl px-3 py-2.5 text-sm outline-none ring-2 ring-[#0A2A4F]/20" />
              )}

              {/* Dropdown panel */}
              {showDropdown && (
                <div className="absolute z-50 top-full mt-1 w-full bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden">
                  {/* Search */}
                  <div className="p-2 border-b border-gray-100">
                    <input autoFocus type="text" placeholder="Search bank…"
                      value={bankSearch} onChange={(e) => setBankSearch(e.target.value)}
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0A2A4F]" />
                  </div>

                  {/* List */}
                  <div className="max-h-64 overflow-y-auto">
                    {filteredBanks.length === 0 ? (
                      <p className="text-center text-xs text-gray-400 py-4">No banks found</p>
                    ) : (
                      Object.entries(
                        filteredBanks.reduce((acc, b) => {
                          if (!acc[b.category]) acc[b.category] = [];
                          acc[b.category].push(b);
                          return acc;
                        }, {})
                      ).map(([cat, banks]) => (
                        <div key={cat}>
                          <p className="px-3 py-1.5 text-[10px] font-black text-gray-400 uppercase tracking-widest bg-gray-50 sticky top-0">{cat}</p>
                          {banks.map((b) => (
                            <button key={b.name} type="button"
                              onClick={() => { setForm({ ...form, mainBank: b.name }); setIsCustomBank(false); setShowDropdown(false); }}
                              className={`w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[#0A2A4F]/5 transition text-left ${form.mainBank === b.name ? "bg-[#0A2A4F]/8" : ""}`}>
                              <img src={b.logo} alt={b.name}
                                className="w-7 h-7 rounded-lg object-contain border border-gray-100 flex-shrink-0"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  e.target.nextSibling.style.display = "flex";
                                }} />
                              <div className="w-7 h-7 rounded-lg bg-[#0A2A4F] items-center justify-center text-white text-[10px] font-black flex-shrink-0 hidden">
                                {b.short[0]}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-gray-800 truncate">{b.name}</p>
                                <p className="text-[10px] text-gray-400">{b.short}</p>
                              </div>
                              {form.mainBank === b.name && <span className="text-[#0A2A4F] text-sm flex-shrink-0">✓</span>}
                            </button>
                          ))}
                        </div>
                      ))
                    )}

                    {/* Other option */}
                    <div className="border-t border-gray-100">
                      <button type="button"
                        onClick={() => { setForm({ ...form, mainBank: "" }); setIsCustomBank(true); setShowDropdown(false); }}
                        className="w-full flex items-center gap-3 px-3 py-3 hover:bg-gray-50 transition text-left">
                        <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <Plus size={14} className="text-gray-500" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-700">Other</p>
                          <p className="text-[10px] text-gray-400">Enter bank name manually</p>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>


          </div>
          {formError && <p className="text-xs text-red-500 font-semibold mb-3 flex items-center gap-1.5">⚠ {formError}</p>}
          <div className="flex gap-3">
            <button type="button" onClick={handleCreate} disabled={creating}
              className={`px-8 py-3 rounded-2xl text-white font-bold text-sm transition ${creating ? "bg-gray-300 cursor-not-allowed" : "bg-[#0A2A4F] hover:bg-[#0d3562]"}`}>
              {creating ? "Adding…" : "Add Main Bank"}
            </button>
            <button type="button" onClick={() => { setShowAddForm(false); setFormError(""); }} className="px-6 py-3 rounded-2xl bg-gray-100 text-gray-600 font-bold text-sm hover:bg-gray-200 transition">Cancel</button>
          </div>
        </div>
      )}

      {/* Sort & Filter toolbar */}
      {!loading && bankGroups.length > 0 && (
        <div className="flex flex-col gap-3 mb-4">

          {/* Row 1: Status filter + sort toggle */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">

            {/* All / Active / Inactive filter */}
            <div className="flex items-center gap-1 bg-white rounded-2xl border border-gray-100 p-1 shadow-sm w-fit">
              {[
                { val: "all",      label: "All Nicks",  activeClass: "bg-[#0A2A4F] text-white" },
                { val: "active",   label: "Active",     activeClass: "bg-emerald-500 text-white" },
                { val: "inactive", label: "Inactive",   activeClass: "bg-red-500 text-white" },
              ].map(({ val, label, activeClass }) => (
                <button key={val} onClick={() => setFilterStatus(val)}
                  className={`px-4 py-1.5 rounded-xl text-xs font-bold transition ${
                    filterStatus === val ? activeClass : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}>
                  {label}
                </button>
              ))}
            </div>

            {/* A→Z / Z→A sort toggle */}
            <button onClick={() => setSortAZ((p) => !p)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-2xl text-xs font-bold text-gray-600 hover:border-[#0A2A4F] hover:text-[#0A2A4F] transition shadow-sm w-fit">
              <span className="font-black">{sortAZ ? "A → Z" : "Z → A"}</span>
              <span className="text-gray-400 text-sm">{sortAZ ? "↓" : "↑"}</span>
            </button>
          </div>

          {/* Row 2: A–Z letter index */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-3 py-2.5 overflow-x-auto">
            <div className="flex items-center gap-1 min-w-max">
              {/* All button */}
              <button onClick={() => setLetterFilter("")}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition ${
                  !letterFilter ? "bg-[#0A2A4F] text-white" : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                }`}>
                All
              </button>
              <span className="w-px h-4 bg-gray-200 mx-1 flex-shrink-0" />
              {/* A–Z letters */}
              {"ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").map((letter) => {
                const hasBank = availableLetters.has(letter);
                const isActive = letterFilter === letter;
                return (
                  <button key={letter}
                    onClick={() => hasBank && setLetterFilter(isActive ? "" : letter)}
                    disabled={!hasBank}
                    className={`w-9 h-9 rounded-xl text-sm font-bold transition flex-shrink-0 ${
                      isActive  ? "bg-[#0A2A4F] text-white shadow-sm"
                      : hasBank ? "text-gray-700 hover:bg-[#0A2A4F]/10 hover:text-[#0A2A4F]"
                                : "text-gray-300 cursor-not-allowed"
                    }`}>
                    {letter}
                  </button>
                );
              })}
            </div>
          </div>

        </div>
      )}

      {/* Bank groups */}
      {loading ? (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm py-20 text-center">
          <div className="w-8 h-8 border-2 border-[#0A2A4F] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-400">Loading banks…</p>
        </div>
      ) : bankGroups.length === 0 ? (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm py-20 text-center">
          <p className="text-gray-400 text-sm">No banks found. Add your first nick.</p>
        </div>
      ) : displayedGroups.length === 0 ? (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm py-16 text-center">
          <p className="text-gray-400 text-sm">
            No banks{letterFilter ? ` starting with "${letterFilter}"` : ""}{filterStatus !== "all" ? ` with ${filterStatus} nicks` : ""} found.
          </p>
          <button onClick={() => { setFilterStatus("all"); setLetterFilter(""); }}
            className="mt-3 text-xs font-bold text-[#0A2A4F] underline underline-offset-2">
            Clear filters
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {chunkArray(displayedGroups, bankCols).map((rowGroups, rowIdx) => {
            const openGroupInRow = rowGroups.find((g) => g.mainBank === openBank);
            return (
              <React.Fragment key={rowIdx}>

                {/* ── Row of bank cards ── */}
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  {rowGroups.map((group) => {
                    const isOpen        = group.mainBank === openBank;
                    const nicks         = group.nicks || [];
                    const originalNicks = (bankGroups.find((g) => g.mainBank === group.mainBank)?.nicks) || [];
                    const activeCount   = originalNicks.filter((n) => n.isActive).length;
                    const bankMeta      = INDIAN_BANKS.find((b) => b.name.toLowerCase() === group.mainBank?.toLowerCase());
                    return (
                      <div key={group.mainBank} className="bg-white rounded-3xl border border-[#0A2A4F]/15 shadow-sm overflow-hidden">
                        <div className="p-5">
                          <div className="flex items-start gap-3 mb-4">
                            {bankMeta ? (
                              <div className="w-14 h-14 rounded-2xl border border-gray-100 bg-white flex items-center justify-center flex-shrink-0 overflow-hidden p-1.5 shadow-sm">
                                <img src={bankMeta.logo} alt={group.mainBank} className="w-full h-full object-contain"
                                  onError={(e) => { e.target.style.display="none"; e.target.parentNode.innerHTML=`<span class='text-[#0A2A4F] text-xl font-black'>${group.mainBank[0]}</span>`; }} />
                              </div>
                            ) : (
                              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#0A2A4F] to-[#1a5298] flex items-center justify-center text-white text-xl font-black flex-shrink-0">
                                {group.mainBank?.[0]?.toUpperCase()}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-base font-black text-gray-900 leading-tight">{group.mainBank}</p>
                              <p className="text-xs text-gray-400 mt-0.5">
                                {originalNicks.length} nick{originalNicks.length !== 1 ? "s" : ""}
                                {filterStatus !== "all" && (
                                  <span className={`ml-1.5 font-bold ${filterStatus === "active" ? "text-emerald-600" : "text-red-500"}`}>· {nicks.length} shown</span>
                                )}
                              </p>
                              <div className="flex items-center gap-1.5 mt-1.5">
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700">{activeCount} active</span>
                                {originalNicks.length - activeCount > 0 && (
                                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-500">{originalNicks.length - activeCount} inactive</span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setQuickForm({ nickName: "", accountNumber: "", ifscCode: "", sections: ["CDM","CASH","ONLINE","QR"] });
                                const nextQuick = quickAdd === group.mainBank ? null : group.mainBank;
                                setQuickAdd(nextQuick);
                                if (nextQuick) { setOpenBank(group.mainBank); setOpenNick(null); }
                              }}
                              className="w-9 h-9 rounded-xl bg-[#0A2A4F] text-white hover:bg-[#0d3562] transition flex items-center justify-center flex-shrink-0"
                              title={`Add nick under ${group.mainBank}`}>
                              {quickAdd === group.mainBank ? <span className="text-lg font-bold leading-none">×</span> : <Plus size={18} />}
                            </button>
                          </div>

                          <div className="flex flex-wrap gap-1.5 mb-3">
                            {nicks.slice(0, 6).map((n) => (
                              <span key={n.id} className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold ${n.isActive ? "bg-[#0A2A4F]/10 text-[#0A2A4F]" : "bg-gray-100 text-gray-400 line-through"}`}>
                                {n.nickName}
                              </span>
                            ))}
                            {nicks.length > 6 && <span className="px-2.5 py-1 rounded-lg text-[10px] text-gray-400 bg-gray-50">+{nicks.length - 6} more</span>}
                          </div>

                          <button
                            onClick={() => { setOpenBank(openBank === group.mainBank ? null : group.mainBank); setOpenNick(null); }}
                            className={`w-full flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-bold transition border ${
                              isOpen ? "bg-[#0A2A4F] text-white border-[#0A2A4F]" : "bg-gray-50 text-gray-600 border-gray-200 hover:border-[#0A2A4F] hover:text-[#0A2A4F]"
                            }`}>
                            {isOpen ? <><ChevronUp size={14} /> Hide Accounts</> : <><ChevronDown size={14} /> View Accounts</>}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {Array.from({ length: bankCols - rowGroups.length }).map((_, i) => (
                    <div key={`bf-${i}`} className="hidden sm:block" />
                  ))}
                </div>

                {/* ── Expansion panel — inserts below this row when a bank in it is open ── */}
                {openGroupInRow && (() => {
                  const group    = openGroupInRow;
                  const nicks    = group.nicks || [];
                  const nickRows = chunkArray(nicks, nickCols);
                  return (
                    <div className="bg-[#0A2A4F]/[0.02] border border-[#0A2A4F]/10 rounded-3xl px-5 py-5">
                      <div className="flex items-center justify-between mb-4">
                        <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
                          Accounts — {group.mainBank}
                        </p>
                        <button onClick={() => { setOpenBank(null); setOpenNick(null); setQuickAdd(null); }}
                          className="text-[10px] font-bold text-gray-400 hover:text-gray-600 px-2 py-1 rounded-lg hover:bg-white transition">
                          ✕ Close
                        </button>
                      </div>

                      {/* Quick-add form */}
                      {quickAdd === group.mainBank && (
                        <div className="mb-5 bg-white rounded-2xl border border-[#0A2A4F]/10 px-5 py-4">
                          <p className="text-[11px] font-black text-[#0A2A4F] uppercase tracking-widest mb-3">Add Sub Bank under {group.mainBank}</p>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                            <div>
                              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">Nick Name *</label>
                              <input type="text" placeholder={`e.g. ${group.mainBank.split(" ")[0].substring(0,6)}-10`}
                                value={quickForm.nickName} onChange={(e) => setQuickForm((p) => ({ ...p, nickName: e.target.value }))} className={inp} />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">Account Number *</label>
                              <input type="text" placeholder="Numeric only"
                                value={quickForm.accountNumber} onChange={(e) => setQuickForm((p) => ({ ...p, accountNumber: e.target.value }))} className={inp} />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">IFSC Code</label>
                              <input type="text" placeholder="e.g. PYTM0123456"
                                value={quickForm.ifscCode} onChange={(e) => setQuickForm((p) => ({ ...p, ifscCode: e.target.value.toUpperCase() }))} className={inp} />
                            </div>
                            <div className="sm:col-span-3">
                              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-2">Sections to Create *</label>
                              <div className="flex items-center gap-2 flex-wrap">
                                {["CDM","CASH","ONLINE","QR"].map((s) => {
                                  const st = SECTION_STYLE[s];
                                  const checked = quickForm.sections.includes(s);
                                  return (
                                    <div key={s} role="checkbox" aria-checked={checked}
                                      onClick={() => setQuickForm((p) => ({ ...p, sections: p.sections.includes(s) ? p.sections.filter((x) => x !== s) : [...p.sections, s] }))}
                                      className={`flex items-center gap-2 px-3 py-2 rounded-xl border-2 cursor-pointer select-none transition ${checked ? `${st.border} ${st.light}` : "border-gray-200 bg-white hover:bg-gray-50"}`}>
                                      <st.Icon size={13} className={checked ? st.text : "text-gray-300"} />
                                      <span className={`text-[11px] font-black transition ${checked ? st.text : "text-gray-400"}`}>{s}</span>
                                      <span className={`w-3 h-3 rounded-full border-2 flex items-center justify-center transition ${checked ? `${st.bg} border-transparent` : "border-gray-300 bg-white"}`}>
                                        {checked && <span className="text-white text-[8px] leading-none">✓</span>}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                          {quickFormError && <p className="text-xs text-red-500 font-semibold mb-2">⚠ {quickFormError}</p>}
                          <div className="flex gap-2">
                            <button type="button" onClick={() => handleQuickCreate(group.mainBank)} disabled={quickCreating}
                              className={`flex-1 py-2.5 rounded-xl text-white text-sm font-bold transition ${quickCreating ? "bg-gray-300 cursor-not-allowed" : "bg-[#0A2A4F] hover:bg-[#0d3562]"}`}>
                              {quickCreating ? "Creating…" : "Add Sub Bank"}
                            </button>
                            <button type="button" onClick={() => { setQuickAdd(null); setQuickFormError(""); }}
                              className="px-5 py-2.5 rounded-xl bg-white border border-gray-200 text-gray-600 text-sm font-bold hover:bg-gray-50 transition">
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Nick rows — each row followed by section expansion if a nick in it is open */}
                      <div className="space-y-3">
                        {nickRows.map((nickRow, nickRowIdx) => {
                          const openNickInRow = nickRow.find((n) => n.id === openNick);
                          return (
                            <React.Fragment key={nickRowIdx}>
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                                {nickRow.map((nick) => (
                                  <NickRow key={nick.id} nick={nick} />
                                ))}
                                {Array.from({ length: nickCols - nickRow.length }).map((_, i) => (
                                  <div key={`nf-${i}`} className="hidden sm:block" />
                                ))}
                              </div>
                              {openNickInRow && <NickSectionsPanel nick={openNickInRow} />}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* ── Configure drawer ── */}
      {configNick && (
        <div className="fixed inset-0 z-50 flex items-center justify-end bg-black/40" onClick={() => setConfigNick(null)}>
          <div className="bg-white h-full w-full max-w-lg overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>

            {/* Drawer header */}
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-5 flex items-center gap-4 z-10">
              {(() => { const bm = INDIAN_BANKS.find((b) => b.name.toLowerCase() === configNick.mainBank?.toLowerCase()); return bm ? (
                <div className="w-10 h-10 rounded-xl border border-gray-200 bg-white flex items-center justify-center flex-shrink-0 overflow-hidden p-1 shadow-sm">
                  <img src={bm.logo} alt={bm.name} className="w-full h-full object-contain"
                    onError={(e) => { e.target.style.display="none"; e.target.parentNode.innerHTML=`<span class='text-[#0A2A4F] text-sm font-black'>${configNick.nickName?.[0]?.toUpperCase()}</span>`; }} />
                </div>
              ) : (
                <div className="w-10 h-10 rounded-xl bg-[#0A2A4F] flex items-center justify-center text-white text-sm font-black flex-shrink-0">
                  {configNick.nickName?.[0]?.toUpperCase()}
                </div>
              ); })()}
              <div className="flex-1 min-w-0">
                <h2 className="text-base font-black text-gray-900">{configNick.nickName}</h2>
                <p className="text-xs text-gray-400">{configNick.mainBank} · A/C {configNick.accountNumber}</p>
              </div>
              <button onClick={() => setConfigNick(null)}
                className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition">✕</button>
            </div>

            {/* 3 section configs */}
            <div className="p-6 space-y-5">
              {SECTIONS.map((section) => {
                const st    = SECTION_STYLE[section];
                const draft = sectionDraft[section] || {};
                const saving= savingSection === section;

                return (
                  <div key={section} className={`rounded-2xl border-2 overflow-hidden ${draft.isActive ? st.border : "border-gray-200"}`}>

                    {/* Section header */}
                    <div className={`${draft.isActive ? st.bg : "bg-gray-400"} px-5 py-3 flex items-center justify-between`}>
                      <p className="text-sm font-black text-white">{section}</p>
                      <button
                        onClick={() => updateDraft(section, "isActive", !draft.isActive)}
                        className={`text-[11px] font-bold px-3 py-1 rounded-full transition ${draft.isActive ? "bg-white/20 text-white hover:bg-white/30" : "bg-white/30 text-white hover:bg-white/40"}`}>
                        {draft.isActive ? "ON — Click to disable" : "OFF — Click to enable"}
                      </button>
                    </div>

                    {/* Fee fields */}
                    <div className="p-5 space-y-3">
                      <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">Fee Type</label>
                        <select value={draft.feeType || "FLAT"}
                          onChange={(e) => updateDraft(section, "feeType", e.target.value)} className={inp}>
                          <option value="FLAT">Flat Amount (₹)</option>
                          <option value="PERCENTAGE">Percentage (%)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">
                          Fee Value {draft.feeType === "PERCENTAGE" ? "(%)" : "(₹)"}
                        </label>
                        <input type="number" step="0.01" min="0" placeholder="0"
                          value={draft.feeValue ?? ""}
                          onChange={(e) => updateDraft(section, "feeValue", e.target.value)}
                          className={inp} />
                      </div>
                      <div className="border-t border-gray-100 pt-3">
                        <p className="text-xs font-black text-orange-600 uppercase tracking-wide mb-2">TP Fee (Third Party)</p>
                        <div>
                          <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">TP Fee Type</label>
                          <select value={draft.tpFeeType || "FLAT"}
                            onChange={(e) => updateDraft(section, "tpFeeType", e.target.value)} className={inp}>
                            <option value="FLAT">Flat Amount (₹)</option>
                            <option value="PERCENTAGE">Percentage (%)</option>
                          </select>
                        </div>
                        <div className="mt-3">
                          <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">
                            TP Fee Value {draft.tpFeeType === "PERCENTAGE" ? "(%)" : "(₹)"}
                          </label>
                          <input type="number" step="0.01" min="0" placeholder="0"
                            value={draft.tpFeeValue ?? ""}
                            onChange={(e) => updateDraft(section, "tpFeeValue", e.target.value)}
                            className={inp} />
                        </div>
                      </div>
                      <button onClick={() => handleSaveSection(section)} disabled={saving}
                        className={`w-full py-3 rounded-xl text-white text-sm font-bold transition ${
                          saving ? "bg-gray-300 cursor-not-allowed" : `${draft.isActive ? st.bg : "bg-gray-500"} hover:opacity-90`
                        }`}>
                        {saving ? "Saving…" : `Save ${section}`}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Confirm popup — used for activate / deactivate / delete nick / delete section */}
      {confirmPopup.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => { setConfirmPopup({ open: false, action: "", title: "", message: "", onConfirm: null }); setPendingSection(null); }}>
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>

            {/* Icon */}
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
              confirmPopup.action === "activate"   ? "bg-emerald-100" :
              confirmPopup.action === "deactivate" ? "bg-amber-100"   : "bg-red-100"
            }`}>
              {confirmPopup.action === "activate"   && <CheckCircle2 size={28} className="text-emerald-600" />}
              {confirmPopup.action === "deactivate" && <Ban           size={28} className="text-amber-600"   />}
              {confirmPopup.action === "delete"     && <Trash2        size={28} className="text-red-500"     />}
            </div>

            {/* Title & message */}
            <h2 className="text-lg font-black text-gray-900 text-center mb-1">{confirmPopup.title}</h2>
            <p className="text-sm text-gray-500 text-center mb-6">{confirmPopup.message}</p>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => { setConfirmPopup({ open: false, action: "", title: "", message: "", onConfirm: null }); setPendingSection(null); }}
                className="flex-1 py-3 rounded-2xl bg-gray-100 text-gray-700 font-bold text-sm hover:bg-gray-200 transition">
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (pendingSection) {
                    // Section delete — uses explicit state, not closure
                    const { nickId, section } = pendingSection;
                    const key = `${nickId}_${section}`;
                    try {
                      setDeletingSection(key);
                      await deleteSectionApi(null, `?id=${nickId}&section=${section}`);
                      fetchBanks(true);
                    } catch (e) { console.error(e); }
                    finally { setDeletingSection(null); setPendingSection(null); }
                  } else if (confirmPopup.onConfirm) {
                    await confirmPopup.onConfirm();
                  }
                  setConfirmPopup({ open: false, action: "", title: "", message: "", onConfirm: null });
                }}
                className={`flex-1 py-3 rounded-2xl text-white font-bold text-sm transition ${
                  confirmPopup.action === "activate"   ? "bg-emerald-600 hover:bg-emerald-700" :
                  confirmPopup.action === "deactivate" ? "bg-amber-500 hover:bg-amber-600"     :
                                                         "bg-red-500 hover:bg-red-600"
                }`}>
                {confirmPopup.action === "activate"   ? "Yes, Activate"   :
                 confirmPopup.action === "deactivate" ? "Yes, Deactivate" : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bankdetails;